import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface CampSchedule {
  id: string;
  start_date: string;
  end_date: string;
  event_year: number | null;
  child: {
    id: string;
    name: string;
  };
  camp: {
    id: string;
    name: string;
    description?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
  };
}

/**
 * Escape special characters for ICS format
 */
export function escapeICS(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
}

/**
 * Format date-only for ICS all-day events (YYYYMMDD)
 */
export function formatICSDateOnly(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

/**
 * Generate and download bulk ICS file for all camps
 */
export function generateBulkICSFile(schedules: CampSchedule[], currentYear: number): void {
  try {
    const now = new Date();
    
    // Filter schedules for current year (or where event_year matches currentYear)
    const yearSchedules = schedules.filter(schedule => {
      // If event_year is set, use it; otherwise infer from start_date
      if (schedule.event_year !== null && schedule.event_year !== undefined) {
        return schedule.event_year === currentYear;
      }
      // Infer year from start_date
      try {
        const startDate = new Date(schedule.start_date + 'T00:00:00');
        return startDate.getUTCFullYear() === currentYear;
      } catch (e) {
        console.error('Error parsing start_date:', schedule.start_date, e);
        return false;
      }
    });

    if (yearSchedules.length === 0) {
      alert(`No camps scheduled for ${currentYear}.`);
      return;
    }

    // Generate ICS content
    let icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//CampWhiz//Bulk Calendar Export//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
X-WR-CALNAME:CampWhiz Camp Schedule ${currentYear}
X-WR-TIMEZONE:America/New_York
`;

    for (const schedule of yearSchedules) {
      try {
        // Use DATE format for all-day events
        const startDate = new Date(schedule.start_date + 'T00:00:00');
        const endDate = new Date(schedule.end_date + 'T00:00:00');
        const endDateExclusive = new Date(endDate);
        endDateExclusive.setDate(endDateExclusive.getDate() + 1); // Add one day for exclusive DTEND

        const eventUid = `campwhiz-${schedule.id}@campwhiz.com`;
        const summary = `${schedule.camp.name} - ${schedule.child.name}`;
        const description = schedule.camp.description
          ? escapeICS(schedule.camp.description)
          : `Camp schedule for ${schedule.child.name}`;

        const locationParts = [
          schedule.camp.address,
          schedule.camp.city,
          schedule.camp.state,
          schedule.camp.zip_code
        ].filter(Boolean);
        const location = locationParts.join(', ') || 'Location TBD';

        icsContent += `BEGIN:VEVENT
UID:${eventUid}
DTSTAMP:${now.toISOString().replace(/[-:]/g, '').split('.')[0]}Z
DTSTART;VALUE=DATE:${formatICSDateOnly(startDate)}
DTEND;VALUE=DATE:${formatICSDateOnly(endDateExclusive)}
SUMMARY:${escapeICS(summary)}
DESCRIPTION:${escapeICS(description)}
LOCATION:${escapeICS(location)}
SEQUENCE:0
STATUS:CONFIRMED
END:VEVENT
`;
      } catch (e) {
        console.error('Error processing schedule:', schedule, e);
        // Continue with next schedule
      }
    }

    icsContent += `END:VCALENDAR`;

    // Create and download file
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `campwhiz-schedule-${currentYear}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Error generating bulk ICS file:', error);
    alert('Failed to generate calendar file. Please check the console for details.');
  }
}

interface BulkCalendarExportProps {
  schedules: CampSchedule[];
  currentYear: number;
}

export default function BulkCalendarExport({ schedules, currentYear }: BulkCalendarExportProps) {
  const currentYearScheduleCount = schedules.filter(schedule => {
    if (schedule.event_year !== null) {
      return schedule.event_year === currentYear;
    }
    const startDate = new Date(schedule.start_date + 'T00:00:00');
    return startDate.getUTCFullYear() === currentYear;
  }).length;

  if (currentYearScheduleCount === 0) {
    return null; // Don't show button if no camps for current year
  }

  return (
    <button
      onClick={() => generateBulkICSFile(schedules, currentYear)}
      className="btn btn-secondary inline-flex items-center gap-1.5"
    >
      <ArrowDownTrayIcon className="h-5 w-5" />
      Download All Camps ({currentYearScheduleCount} camps)
    </button>
  );
}
